import React, { useState } from "react";
import { graphql, useMutation } from "react-relay";
import { Button } from "src/components/ui/button";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import { REDACTED, SecretInput } from "src/components/ui/secret-input";
import { ErrorAlert, SuccessAlert } from "src/components/ui/status-alert";
import { Tooltip } from "src/components/ui/tooltip";
import { usePermissions } from "src/context/MeContext";

import type { IntegrationType } from "./__generated__/IntegrationsSection_organization.graphql";
import type { PostgreSQLIntegrationFormUpdateIntegrationMutation } from "./__generated__/PostgreSQLIntegrationFormUpdateIntegrationMutation.graphql";
import type { PostgreSQLIntegrationFormValidateIntegrationMutation } from "./__generated__/PostgreSQLIntegrationFormValidateIntegrationMutation.graphql";

const updateIntegrationMutation = graphql`
  mutation PostgreSQLIntegrationFormUpdateIntegrationMutation(
    $input: UpdateIntegrationInput!
  ) {
    updateIntegration(input: $input) {
      integration {
        id
        name
        integrationType
        credentials
      }
      error
    }
  }
`;

const validateIntegrationMutation = graphql`
  mutation PostgreSQLIntegrationFormValidateIntegrationMutation(
    $input: ValidateIntegrationInput!
  ) {
    validateIntegration(input: $input) {
      success
      error
    }
  }
`;

const SSL_MODES = [
  { value: "disable", label: "Disable" },
  { value: "require", label: "Require (default)" },
  { value: "verify-ca", label: "Verify CA" },
  { value: "verify-full", label: "Verify Full" },
];

interface PostgreSQLIntegrationFormProps {
  onComplete: (success: boolean) => void;
  onCancel: () => void;
  onDisconnect?: () => void;
  integrationId?: string;
  initialHost?: string;
  initialPort?: number;
  initialUser?: string;
  initialPassword?: string;
  initialDatabase?: string;
  initialSslmode?: string;
}

export default function PostgreSQLIntegrationForm({
  onComplete,
  onCancel,
  onDisconnect,
  integrationId,
  initialHost,
  initialPort,
  initialUser,
  initialPassword,
  initialDatabase,
  initialSslmode,
}: PostgreSQLIntegrationFormProps) {
  const [host, setHost] = useState(initialHost || "");
  const [port, setPort] = useState(initialPort?.toString() || "5432");
  const [user, setUser] = useState(initialUser || "");
  const [password, setPassword] = useState(initialPassword ? REDACTED : "");
  const [database, setDatabase] = useState(initialDatabase || "");
  const [sslmode, setSslmode] = useState(initialSslmode || "require");
  const [formError, setFormError] = useState<string | null>(null);
  const [validationSuccess, setValidationSuccess] = useState<string | null>(null);
  const [isValidated, setIsValidated] = useState(false);
  const { canWrite } = usePermissions();
  const canModify = canWrite("integrations");

  const [commitMutation, isMutationInFlight] = useMutation<PostgreSQLIntegrationFormUpdateIntegrationMutation>(
    updateIntegrationMutation,
  );
  const [commitValidateMutation, isValidationInFlight] =
    useMutation<PostgreSQLIntegrationFormValidateIntegrationMutation>(validateIntegrationMutation);

  const resetValidation = () => {
    setIsValidated(false);
    setValidationSuccess(null);
  };

  const buildCredentials = () => {
    return {
      host: host.trim(),
      port: parseInt(port.trim(), 10) || 5432,
      user: user.trim(),
      password: password.trim(),
      database: database.trim(),
      sslmode: sslmode,
    };
  };

  const handleTest = () => {
    setFormError(null);
    setValidationSuccess(null);
    setIsValidated(false);

    const parsedPort = parseInt(port.trim(), 10);
    if (isNaN(parsedPort) || parsedPort < 1 || parsedPort > 65535) {
      setFormError("Port must be a number between 1 and 65535");
      return;
    }
    if (!host.trim()) {
      setFormError("Host is required");
      return;
    }
    if (!user.trim()) {
      setFormError("Username is required");
      return;
    }
    if (!password.trim()) {
      setFormError("Password is required");
      return;
    }
    if (!database.trim()) {
      setFormError("Database name is required");
      return;
    }

    const input = {
      integrationType: "POSTGRESQL" as IntegrationType,
      credentials: JSON.stringify(buildCredentials()),
      ...(integrationId && { integrationId }),
    };

    commitValidateMutation({
      variables: { input },
      onCompleted: (response, errors) => {
        if (errors && errors.length > 0) {
          setFormError(errors.map((e) => e.message).join(", "));
          return;
        }
        if (!response.validateIntegration.success) {
          setFormError(response.validateIntegration.error || "Validation failed");
          return;
        }
        setValidationSuccess("Connection validated successfully!");
        setIsValidated(true);
      },
      onError: (error) => {
        setFormError(error.message);
      },
    });
  };

  const handleSave = () => {
    setFormError(null);
    setValidationSuccess(null);

    const input = {
      integrationType: "POSTGRESQL" as IntegrationType,
      credentials: JSON.stringify(buildCredentials()),
      ...(integrationId && { integrationId }),
    };

    commitMutation({
      variables: { input },
      onCompleted: (response, errors) => {
        if (errors && errors.length > 0) {
          setFormError(errors.map((e) => e.message).join(", "));
          onComplete(false);
          return;
        }
        if (response.updateIntegration.error) {
          setFormError(response.updateIntegration.error);
          onComplete(false);
          return;
        }
        onComplete(true);
      },
      onError: (error) => {
        setFormError(error.message);
        onComplete(false);
      },
    });
  };

  return (
    <div className="space-y-6">
      <div className="space-y-6">
        <div className="border-t border-border pt-4">
          <h3 className="text-base font-semibold text-foreground mb-4">Connection Configuration</h3>

          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2 space-y-2">
                <Label htmlFor="pg-host" className="text-muted-foreground">
                  Host
                </Label>
                <Tooltip
                  content={!canModify ? "You don't have permission to modify integrations" : null}
                  position="top"
                  asChild
                >
                  <span className="block w-full">
                    <Input
                      id="pg-host"
                      type="text"
                      className="placeholder:opacity-50"
                      value={host}
                      onChange={(e) => {
                        setHost(e.target.value);
                        resetValidation();
                      }}
                      placeholder="e.g. db.example.com"
                      disabled={!canModify}
                    />
                  </span>
                </Tooltip>
              </div>

              <div className="space-y-2">
                <Label htmlFor="pg-port" className="text-muted-foreground">
                  Port
                </Label>
                <Input
                  id="pg-port"
                  type="text"
                  className="placeholder:opacity-50"
                  value={port}
                  onChange={(e) => {
                    setPort(e.target.value);
                    resetValidation();
                  }}
                  placeholder="5432"
                  disabled={!canModify}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="pg-database" className="text-muted-foreground">
                Database
              </Label>
              <Input
                id="pg-database"
                type="text"
                className="placeholder:opacity-50"
                value={database}
                onChange={(e) => {
                  setDatabase(e.target.value);
                  resetValidation();
                }}
                placeholder="e.g. production"
                disabled={!canModify}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="pg-user" className="text-muted-foreground">
                Username
              </Label>
              <Input
                id="pg-user"
                type="text"
                className="placeholder:opacity-50"
                value={user}
                onChange={(e) => {
                  setUser(e.target.value);
                  resetValidation();
                }}
                placeholder="e.g. readonly_user"
                disabled={!canModify}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="pg-password" className="text-muted-foreground">
                Password
              </Label>
              <Tooltip
                content={!canModify ? "You don't have permission to modify integrations" : null}
                position="top"
                asChild
              >
                <span className="block w-full">
                  <SecretInput
                    id="pg-password"
                    className="placeholder:opacity-50"
                    value={password}
                    fieldLabel="Password"
                    onChange={(e) => {
                      setPassword(e.target.value);
                      resetValidation();
                    }}
                    placeholder="Enter password"
                    disabled={!canModify}
                  />
                </span>
              </Tooltip>
            </div>

            <div className="space-y-2">
              <Label htmlFor="pg-sslmode" className="text-muted-foreground">
                SSL Mode
              </Label>
              <select
                id="pg-sslmode"
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                value={sslmode}
                onChange={(e) => {
                  setSslmode(e.target.value);
                  resetValidation();
                }}
                disabled={!canModify}
              >
                {SSL_MODES.map((mode) => (
                  <option key={mode.value} value={mode.value}>
                    {mode.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {formError && <ErrorAlert icon="none">{formError}</ErrorAlert>}

      {validationSuccess && <SuccessAlert icon="none">{validationSuccess}</SuccessAlert>}

      <div className="flex items-center pt-2">
        {onDisconnect && (
          <Button
            variant="destructive"
            size="sm"
            onClick={onDisconnect}
            disabled={isMutationInFlight || isValidationInFlight}
          >
            Disconnect
          </Button>
        )}
        <div className="flex ml-auto space-x-3">
        <Button variant="outline" onClick={onCancel} disabled={isMutationInFlight || isValidationInFlight}>
          Cancel
        </Button>
        <Tooltip content={!canModify ? "You don't have permission to connect integrations" : null} position="top">
          <span className="inline-block">
            <Button
              variant="outline"
              onClick={handleTest}
              disabled={isMutationInFlight || isValidationInFlight || !canModify}
            >
              {isValidationInFlight ? "Testing..." : "Test Connection"}
            </Button>
          </span>
        </Tooltip>
        <div className="relative group">
          <Tooltip content={!canModify ? "You don't have permission to connect integrations" : null} position="top">
            <span className="inline-block">
              <Button
                onClick={handleSave}
                disabled={!isValidated || isMutationInFlight || isValidationInFlight || !canModify}
              >
                {isMutationInFlight ? "Saving..." : "Connect"}
              </Button>
            </span>
          </Tooltip>
          {!isValidated && !isMutationInFlight && !isValidationInFlight && canModify && (
            <div className="absolute bottom-full mb-2 right-0 px-3 py-2 bg-card text-foreground text-sm rounded opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap">
              To connect, please test the connection.
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
}
